/**
 * WebRTC Diagnostics Module
 *
 * Collects WebRTC performance metrics from PixelStreaming sessions and
 * formats them for PostHog analytics. Useful for diagnosing visual artifacts
 * like freezing, stuttering, and pixelation.
 *
 * Configuration via environment variables:
 * - VITE_WEBRTC_DIAGNOSTICS_ENABLED: 'true' to enable diagnostics
 * - VITE_WEBRTC_DIAGNOSTICS_SAMPLE_RATE_MS: sampling interval in milliseconds
 */

import type { AggregatedStats } from "@epicgames-ps/lib-pixelstreamingfrontend-ue5.6";

// Configuration interface
export interface WebRTCDiagnosticsConfig {
  enabled: boolean;
  sampleRateMs: number;
}

// Get diagnostics configuration from environment
export function getWebRTCDiagnosticsConfig(): WebRTCDiagnosticsConfig {
  return {
    enabled: import.meta.env.VITE_WEBRTC_DIAGNOSTICS_ENABLED === "true",
    sampleRateMs: parseInt(
      import.meta.env.VITE_WEBRTC_DIAGNOSTICS_SAMPLE_RATE_MS || "5000",
      10
    ),
  };
}

// Numerical statistics with mean, standard deviation, min, and max
export interface NumericalStats {
  mean: number;
  stddev: number;
  min: number;
  max: number;
  count: number;
}

/**
 * Online statistics accumulator using Welford's algorithm.
 * Computes mean, variance, min, and max incrementally without storing all observations.
 * Welford's algorithm provides numerical stability by avoiding catastrophic cancellation.
 */
export class OnlineStatsAccumulator {
  private count: number = 0;
  private mean: number = 0;
  private m2: number = 0; // Sum of squared differences from mean
  private min: number = Infinity;
  private max: number = -Infinity;

  /**
   * Add a new observation to the accumulator.
   */
  update(value: number): void {
    this.count++;

    // Update min/max
    if (value < this.min) this.min = value;
    if (value > this.max) this.max = value;

    // Welford's algorithm for mean and variance
    const delta = value - this.mean;
    this.mean += delta / this.count;
    const delta2 = value - this.mean;
    this.m2 += delta * delta2;
  }

  /**
   * Get current statistics.
   */
  getStats(): NumericalStats {
    if (this.count === 0) {
      return { mean: 0, stddev: 0, min: 0, max: 0, count: 0 };
    }

    if (this.count === 1) {
      return {
        mean: this.mean,
        stddev: 0,
        min: this.min,
        max: this.max,
        count: this.count,
      };
    }

    const variance = this.m2 / (this.count - 1);
    const stddev = Math.sqrt(variance);

    return {
      mean: this.mean,
      stddev,
      min: this.min,
      max: this.max,
      count: this.count,
    };
  }

  /**
   * Reset the accumulator to initial state.
   */
  reset(): void {
    this.count = 0;
    this.mean = 0;
    this.m2 = 0;
    this.min = Infinity;
    this.max = -Infinity;
  }
}

// Video quality metrics - focus on freezing/stuttering and pixelation
export interface VideoQualityMetrics {
  // Instantaneous metrics (accumulate raw values)
  framesPerSecond: NumericalStats; // Current FPS
  bitrate: NumericalStats; // Current bitrate in kbps
  jitter: NumericalStats; // Jitter in seconds
  frameWidth: NumericalStats; // Video width
  frameHeight: NumericalStats; // Video height

  // Cumulative metrics (accumulate deltas between observations)
  freezeCount: NumericalStats; // Number of freeze events
  totalFreezesDuration: NumericalStats; // Total duration of freezes in seconds
  framesDropped: NumericalStats; // Frames dropped during rendering
  framesDecoded: NumericalStats; // Total frames decoded
  packetsLost: NumericalStats; // Packets lost (causes artifacts)
  nackCount: NumericalStats; // Negative acknowledgment count (retransmit requests)
  pliCount: NumericalStats; // Picture Loss Indication count
}

// Audio quality metrics
export interface AudioQualityMetrics {
  // Instantaneous metrics
  jitter: NumericalStats; // Audio jitter in seconds
  audioLevel: NumericalStats; // Audio level (0-1), count will be 0 if unavailable

  // Cumulative metrics (accumulate deltas)
  packetsLost: NumericalStats; // Audio packets lost
  concealmentEvents: NumericalStats; // Audio concealment events, count will be 0 if unavailable
}

// Network metrics from ICE candidate pairs
export interface NetworkMetrics {
  // Instantaneous metrics
  roundTripTimeMs: NumericalStats; // Current RTT in milliseconds, count will be 0 if no candidate pair

  // Cumulative metrics (accumulate deltas)
  bytesReceived: NumericalStats; // Total bytes received
  bytesSent: NumericalStats; // Total bytes sent
}

// Complete diagnostics sample
export interface WebRTCDiagnosticsSample {
  timestamp: number; // Unix timestamp in milliseconds
  sessionId: string; // Session ID for correlation
  sampleIndex: number; // Sequential sample number (since session start)

  video: VideoQualityMetrics;
  audio: AudioQualityMetrics;
  network: NetworkMetrics;
}

/**
 * Collector class that continuously accumulates WebRTC stats and reports aggregated statistics.
 * Uses online statistical aggregation to capture all observations between reporting intervals.
 */
export class WebRTCDiagnosticsCollector {
  private config: WebRTCDiagnosticsConfig;
  private sessionId: string;
  private sampleIndex: number = 0;
  private lastSampleTime: number = 0;
  private isFirstObservation: boolean = true;

  // Instantaneous metric accumulators (accumulate raw values)
  private fpsAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private bitrateAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private videoJitterAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private frameWidthAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private frameHeightAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private audioJitterAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private audioLevelAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private rttAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();

  // Cumulative metric accumulators (accumulate deltas)
  private freezeCountAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private totalFreezesDurationAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private framesDroppedAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private framesDecodedAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private videoPacketsLostAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private nackCountAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private pliCountAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private audioPacketsLostAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private concealmentEventsAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private bytesReceivedAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();
  private bytesSentAccumulator: OnlineStatsAccumulator = new OnlineStatsAccumulator();

  // Tracking previous cumulative values for delta computation
  private lastFreezeCount: number = 0;
  private lastTotalFreezesDuration: number = 0;
  private lastFramesDecoded: number = 0;
  private lastFramesDropped: number = 0;
  private lastVideoPacketsLost: number = 0;
  private lastNackCount: number = 0;
  private lastPliCount: number = 0;
  private lastAudioPacketsLost: number = 0;
  private lastConcealmentEvents: number = 0;
  private lastBytesReceived: number = 0;
  private lastBytesSent: number = 0;

  constructor(sessionId: string, config?: Partial<WebRTCDiagnosticsConfig>) {
    this.sessionId = sessionId;
    this.config = { ...getWebRTCDiagnosticsConfig(), ...config };
  }

  /**
   * Check if enough time has elapsed to collect a new sample.
   */
  shouldSample(): boolean {
    if (!this.config.enabled) return false;
    const now = Date.now();
    return now - this.lastSampleTime >= this.config.sampleRateMs;
  }

  /**
   * Continuously accumulates WebRTC statistics on every call.
   * Returns aggregated sample only when reporting interval has elapsed.
   */
  collectSample(stats: AggregatedStats): WebRTCDiagnosticsSample | null {
    if (!this.config.enabled) return null;

    const activeCandidatePair = stats.getActiveCandidatePair();

    // Extract current values
    const currentFreezeCount = stats.inboundVideoStats.freezeCount ?? 0;
    const currentTotalFreezesDuration = stats.inboundVideoStats.totalFreezesDuration ?? 0;
    const currentFramesDropped = stats.inboundVideoStats.framesDropped ?? 0;
    const currentFramesDecoded = stats.inboundVideoStats.framesDecoded ?? 0;
    const currentVideoPacketsLost = stats.inboundVideoStats.packetsLost ?? 0;
    const currentNackCount = stats.inboundVideoStats.nackCount ?? 0;
    const currentPliCount = stats.inboundVideoStats.pliCount ?? 0;
    const currentAudioPacketsLost = stats.inboundAudioStats.packetsLost ?? 0;
    const currentBytesReceived = activeCandidatePair?.bytesReceived ?? 0;
    const currentBytesSent = activeCandidatePair?.bytesSent ?? 0;

    // Handle first observation - initialize tracking but don't accumulate deltas
    if (this.isFirstObservation) {
      this.lastFreezeCount = currentFreezeCount;
      this.lastTotalFreezesDuration = currentTotalFreezesDuration;
      this.lastFramesDecoded = currentFramesDecoded;
      this.lastFramesDropped = currentFramesDropped;
      this.lastVideoPacketsLost = currentVideoPacketsLost;
      this.lastNackCount = currentNackCount;
      this.lastPliCount = currentPliCount;
      this.lastAudioPacketsLost = currentAudioPacketsLost;
      this.lastBytesReceived = currentBytesReceived;
      this.lastBytesSent = currentBytesSent;

      // Handle concealmentEvents which can be undefined
      const concealmentEvents = stats.inboundAudioStats.concealmentEvents;
      if (concealmentEvents !== undefined) {
        this.lastConcealmentEvents = concealmentEvents;
      }

      // Accumulate instantaneous metrics on first observation
      this.fpsAccumulator.update(stats.inboundVideoStats.framesPerSecond ?? 0);
      this.bitrateAccumulator.update(stats.inboundVideoStats.bitrate ?? 0);
      this.videoJitterAccumulator.update(stats.inboundVideoStats.jitter ?? 0);
      this.frameWidthAccumulator.update(stats.inboundVideoStats.frameWidth ?? 0);
      this.frameHeightAccumulator.update(stats.inboundVideoStats.frameHeight ?? 0);
      this.audioJitterAccumulator.update(stats.inboundAudioStats.jitter ?? 0);

      const audioLevel = stats.inboundAudioStats.audioLevel;
      if (audioLevel !== undefined) {
        this.audioLevelAccumulator.update(audioLevel);
      }

      if (activeCandidatePair?.currentRoundTripTime !== undefined) {
        this.rttAccumulator.update(activeCandidatePair.currentRoundTripTime * 1000);
      }

      this.isFirstObservation = false;
      return null; // Don't return sample on first observation
    }

    // Compute deltas for cumulative metrics
    const freezeCountDelta = currentFreezeCount - this.lastFreezeCount;
    const freezesDurationDelta = currentTotalFreezesDuration - this.lastTotalFreezesDuration;
    const framesDroppedDelta = currentFramesDropped - this.lastFramesDropped;
    const framesDecodedDelta = currentFramesDecoded - this.lastFramesDecoded;
    const videoPacketsLostDelta = currentVideoPacketsLost - this.lastVideoPacketsLost;
    const nackCountDelta = currentNackCount - this.lastNackCount;
    const pliCountDelta = currentPliCount - this.lastPliCount;
    const audioPacketsLostDelta = currentAudioPacketsLost - this.lastAudioPacketsLost;
    const bytesReceivedDelta = currentBytesReceived - this.lastBytesReceived;
    const bytesSentDelta = currentBytesSent - this.lastBytesSent;

    // Accumulate instantaneous metrics
    this.fpsAccumulator.update(stats.inboundVideoStats.framesPerSecond ?? 0);
    this.bitrateAccumulator.update(stats.inboundVideoStats.bitrate ?? 0);
    this.videoJitterAccumulator.update(stats.inboundVideoStats.jitter ?? 0);
    this.frameWidthAccumulator.update(stats.inboundVideoStats.frameWidth ?? 0);
    this.frameHeightAccumulator.update(stats.inboundVideoStats.frameHeight ?? 0);
    this.audioJitterAccumulator.update(stats.inboundAudioStats.jitter ?? 0);

    const audioLevel = stats.inboundAudioStats.audioLevel;
    if (audioLevel !== undefined) {
      this.audioLevelAccumulator.update(audioLevel);
    }

    if (activeCandidatePair?.currentRoundTripTime !== undefined) {
      this.rttAccumulator.update(activeCandidatePair.currentRoundTripTime * 1000);
    }

    // Accumulate cumulative metric deltas
    this.freezeCountAccumulator.update(freezeCountDelta);
    this.totalFreezesDurationAccumulator.update(freezesDurationDelta);
    this.framesDroppedAccumulator.update(framesDroppedDelta);
    this.framesDecodedAccumulator.update(framesDecodedDelta);
    this.videoPacketsLostAccumulator.update(videoPacketsLostDelta);
    this.nackCountAccumulator.update(nackCountDelta);
    this.pliCountAccumulator.update(pliCountDelta);
    this.audioPacketsLostAccumulator.update(audioPacketsLostDelta);
    this.bytesReceivedAccumulator.update(bytesReceivedDelta);
    this.bytesSentAccumulator.update(bytesSentDelta);

    // Handle concealmentEvents which can be undefined
    const concealmentEvents = stats.inboundAudioStats.concealmentEvents;
    if (concealmentEvents !== undefined) {
      const concealmentEventsDelta = concealmentEvents - this.lastConcealmentEvents;
      this.concealmentEventsAccumulator.update(concealmentEventsDelta);
      this.lastConcealmentEvents = concealmentEvents;
    }

    // Update last values for next iteration
    this.lastFreezeCount = currentFreezeCount;
    this.lastTotalFreezesDuration = currentTotalFreezesDuration;
    this.lastFramesDecoded = currentFramesDecoded;
    this.lastFramesDropped = currentFramesDropped;
    this.lastVideoPacketsLost = currentVideoPacketsLost;
    this.lastNackCount = currentNackCount;
    this.lastPliCount = currentPliCount;
    this.lastAudioPacketsLost = currentAudioPacketsLost;
    this.lastBytesReceived = currentBytesReceived;
    this.lastBytesSent = currentBytesSent;

    // Only return sample if interval has elapsed
    if (!this.shouldSample()) {
      return null;
    }

    // Build aggregated sample
    const video: VideoQualityMetrics = {
      framesPerSecond: this.fpsAccumulator.getStats(),
      bitrate: this.bitrateAccumulator.getStats(),
      jitter: this.videoJitterAccumulator.getStats(),
      frameWidth: this.frameWidthAccumulator.getStats(),
      frameHeight: this.frameHeightAccumulator.getStats(),
      freezeCount: this.freezeCountAccumulator.getStats(),
      totalFreezesDuration: this.totalFreezesDurationAccumulator.getStats(),
      framesDropped: this.framesDroppedAccumulator.getStats(),
      framesDecoded: this.framesDecodedAccumulator.getStats(),
      packetsLost: this.videoPacketsLostAccumulator.getStats(),
      nackCount: this.nackCountAccumulator.getStats(),
      pliCount: this.pliCountAccumulator.getStats(),
    };

    const audio: AudioQualityMetrics = {
      jitter: this.audioJitterAccumulator.getStats(),
      audioLevel: this.audioLevelAccumulator.getStats(),
      packetsLost: this.audioPacketsLostAccumulator.getStats(),
      concealmentEvents: this.concealmentEventsAccumulator.getStats(),
    };

    const network: NetworkMetrics = {
      roundTripTimeMs: this.rttAccumulator.getStats(),
      bytesReceived: this.bytesReceivedAccumulator.getStats(),
      bytesSent: this.bytesSentAccumulator.getStats(),
    };

    const sample: WebRTCDiagnosticsSample = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sampleIndex: this.sampleIndex++,
      video,
      audio,
      network,
    };

    // Reset accumulators for next interval
    this.resetAccumulators();
    this.lastSampleTime = Date.now();

    return sample;
  }

  /**
   * Reset all accumulators and counters for next sampling interval.
   */
  private resetAccumulators(): void {
    this.fpsAccumulator.reset();
    this.bitrateAccumulator.reset();
    this.videoJitterAccumulator.reset();
    this.frameWidthAccumulator.reset();
    this.frameHeightAccumulator.reset();
    this.audioJitterAccumulator.reset();
    this.audioLevelAccumulator.reset();
    this.rttAccumulator.reset();
    this.freezeCountAccumulator.reset();
    this.totalFreezesDurationAccumulator.reset();
    this.framesDroppedAccumulator.reset();
    this.framesDecodedAccumulator.reset();
    this.videoPacketsLostAccumulator.reset();
    this.nackCountAccumulator.reset();
    this.pliCountAccumulator.reset();
    this.audioPacketsLostAccumulator.reset();
    this.concealmentEventsAccumulator.reset();
    this.bytesReceivedAccumulator.reset();
    this.bytesSentAccumulator.reset();
  }

  /**
   * Reset the collector state. Call when starting a new session.
   */
  reset(): void {
    this.sampleIndex = 0;
    this.lastSampleTime = 0;
    this.isFirstObservation = true;

    // Reset all accumulators
    this.resetAccumulators();

    // Reset last cumulative values
    this.lastFreezeCount = 0;
    this.lastTotalFreezesDuration = 0;
    this.lastFramesDecoded = 0;
    this.lastFramesDropped = 0;
    this.lastVideoPacketsLost = 0;
    this.lastNackCount = 0;
    this.lastPliCount = 0;
    this.lastAudioPacketsLost = 0;
    this.lastConcealmentEvents = 0;
    this.lastBytesReceived = 0;
    this.lastBytesSent = 0;
  }

  /**
   * Check if diagnostics are enabled.
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }
}

/**
 * Flatten a NumericalStats object into PostHog-compatible flat properties.
 * Example: flattenStats('video_fps', stats) produces:
 *   { video_fps_mean: 60, video_fps_stddev: 2.5, ... }
 */
function flattenStats(
  prefix: string,
  stats: NumericalStats
): Record<string, number> {
  return {
    [`${prefix}_mean`]: stats.mean,
    [`${prefix}_stddev`]: stats.stddev,
    [`${prefix}_min`]: stats.min,
    [`${prefix}_max`]: stats.max,
    [`${prefix}_count`]: stats.count,
  };
}

/**
 * Convert a diagnostics sample to a flat PostHog event payload.
 * PostHog works best with flat property structures for querying.
 */
export function createPostHogEvent(
  sample: WebRTCDiagnosticsSample
): Record<string, unknown> {
  return {
    // Identifiers
    session_id: sample.sessionId,
    sample_index: sample.sampleIndex,

    // Video quality metrics (flattened)
    ...flattenStats('video_freeze_count', sample.video.freezeCount),
    ...flattenStats('video_freezes_duration_sec', sample.video.totalFreezesDuration),
    ...flattenStats('video_frames_dropped', sample.video.framesDropped),
    ...flattenStats('video_fps', sample.video.framesPerSecond),
    ...flattenStats('video_frames_decoded', sample.video.framesDecoded),
    ...flattenStats('video_packets_lost', sample.video.packetsLost),
    ...flattenStats('video_jitter_sec', sample.video.jitter),
    ...flattenStats('video_nack_count', sample.video.nackCount),
    ...flattenStats('video_pli_count', sample.video.pliCount),
    ...flattenStats('video_bitrate_kbps', sample.video.bitrate),
    ...flattenStats('video_width', sample.video.frameWidth),
    ...flattenStats('video_height', sample.video.frameHeight),

    // Audio quality metrics (flattened)
    ...flattenStats('audio_jitter_sec', sample.audio.jitter),
    ...flattenStats('audio_packets_lost', sample.audio.packetsLost),
    ...flattenStats('audio_level', sample.audio.audioLevel),
    ...flattenStats('audio_concealment_events', sample.audio.concealmentEvents),

    // Network metrics (flattened)
    ...flattenStats('network_rtt_ms', sample.network.roundTripTimeMs),
    ...flattenStats('network_bytes_received', sample.network.bytesReceived),
    ...flattenStats('network_bytes_sent', sample.network.bytesSent),
  };
}
